/**
 * Turning a Claude Code tool call into what policy and people read: the subject a pattern is
 * matched against, a one-line title, a risk level and the pattern an answer should cover.
 */
import type { PolicyRequest, Risk } from '@orgus/protocol';
import { programOf, splitCommand } from './command.ts';
import { SHELL_TOOLS, FILE_WRITE_TOOLS, _internal } from './defaults.ts';
import { relativeTo } from './glob.ts';

const rec = (x: unknown): Record<string, unknown> => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Record<string, unknown>) : {});
const s = (x: unknown): string | undefined => (typeof x === 'string' && x ? x : undefined);

function clipLine(text: string, max: number): string {
  const one = text.replace(/\s+/g, ' ').trim();
  return one.length > max ? `${one.slice(0, max - 1)}…` : one;
}

/** The command or path policy patterns are matched against, for a tool and its input. */
export function subjectOf(tool: string, input: unknown): string | undefined {
  const i = rec(input);
  const t = tool.toLowerCase();
  if (SHELL_TOOLS.has(t)) return s(i.command);
  if (t === 'webfetch') return s(i.url);
  if (t === 'websearch') return s(i.query);
  if (t === 'glob' || t === 'grep') return s(i.path) ?? s(i.pattern);
  if (t === 'task' || t === 'agent') return s(i.subagent_type) ?? s(i.description);
  const path = s(i.file_path) ?? s(i.notebook_path) ?? s(i.path);
  if (path) return path;
  if (t.startsWith('mcp__')) {
    const text = JSON.stringify(input ?? {});
    return text.length > 500 ? text.slice(0, 500) : text;
  }
  return undefined;
}

/** "Run npm test", "Edit src/app.ts", "Fetch https://example.com". */
export function titleOf(tool: string, input: unknown, cwd?: string): string {
  const t = tool.toLowerCase();
  const subject = subjectOf(tool, input);
  const shown = subject && cwd ? (relativeTo(subject, cwd) ?? subject) : subject;
  if (SHELL_TOOLS.has(t)) return subject ? `Run ${clipLine(subject, 120)}` : `Run a ${tool} command`;
  if (t === 'edit' || t === 'multiedit') return `Edit ${shown ?? 'a file'}`;
  if (t === 'write') return `Write ${shown ?? 'a file'}`;
  if (t === 'notebookedit') return `Edit notebook ${shown ?? ''}`.trim();
  if (t === 'read') return `Read ${shown ?? 'a file'}`;
  if (t === 'webfetch') return `Fetch ${clipLine(subject ?? 'a web page', 120)}`;
  if (t === 'websearch') return `Search the web for ${clipLine(subject ?? '', 100)}`.trim();
  if (t.startsWith('mcp__')) {
    const [, server, name] = tool.split('__');
    return `Use ${name || tool} from ${server || 'an MCP server'}`;
  }
  return `Use ${tool}`;
}

const HIGH: RegExp[] = [
  /\bgit\s+push\b.*(?:\s-f\b|--force\b|--force-with-lease\b|\s\+\S)/,
  /\brm\s+(?:-\S*\s+)*-\S*[rR]/,
  /\b(?:sudo|doas|su)\b/,
  /\bgit\s+(?:reset\s+--hard|clean\s+-\S*f|filter-branch|filter-repo)\b/,
  /\b(?:deploy|terraform\s+(?:apply|destroy)|kubectl\s+(?:apply|delete)|wrangler\s+(?:deploy|publish)|vercel\s+--prod)\b/i,
  /(?:^|[\s/\\])(?:\.env(?:\.\w+)?|id_rsa|id_ed25519|\.npmrc|\.pypirc|credentials(?:\.json)?|secrets?\.\w+)(?:\s|$|["'])/i,
  /\b(?:drop\s+(?:table|database)|truncate\s+table)\b/i,
  /\bchmod\s+(?:-R\s+)?777\b/,
];

/**
 * How careful a person should be: high for force pushes, recursive deletes, sudo, deploys and
 * secrets; medium for the network and writes outside the session folder; low otherwise.
 */
export function classifyRisk(req: PolicyRequest): Risk {
  const tool = (req.tool ?? '').toLowerCase();
  const subject = req.subject ?? '';
  if (!req.tool) return req.action === 'session.stop' || req.action === 'worktree.remove' || req.action === 'pipeline.restart' ? 'medium' : 'low';
  if (SHELL_TOOLS.has(tool)) {
    const parts = splitCommand(subject);
    if (parts.some((p) => _internal.isCatastrophic(p)) || HIGH.some((re) => re.test(subject))) return 'high';
    if (parts.some((p) => _internal.isNetworkCommand(p) || _internal.writesOutside(p, req.cwd))) return 'medium';
    return 'low';
  }
  if (FILE_WRITE_TOOLS.has(tool)) {
    if (HIGH[5]!.test(` ${subject} `)) return 'high';
    if (!req.cwd || relativeTo(subject, req.cwd) == null || relativeTo(subject, req.cwd)!.startsWith('..')) return 'medium';
    return 'low';
  }
  if (tool === 'webfetch' || tool === 'websearch' || tool.startsWith('mcp__')) return 'medium';
  return 'low';
}

/**
 * The pattern an "Allow for session" or "Make it a rule" answer covers by default:
 * 'npm test:*' for a command (program plus its subcommand), the folder for a file
 * ('src/lib/*'), the site for a URL. Empty means "any use of this tool".
 */
export function suggestPattern(tool: string, subject: string | undefined, cwd?: string): string {
  if (!subject) return '';
  const t = tool.toLowerCase();
  if (SHELL_TOOLS.has(t)) {
    const first = splitCommand(subject)[0] ?? subject;
    const { program, args } = programOf(first);
    if (!program) return subject;
    const words = first.split(/\s+/);
    const at = words.findIndex((w) => w.replace(/^["']|["']$/g, '').replace(/\\/g, '/').split('/').pop()!.replace(/\.(exe|cmd|bat|ps1)$/i, '') === program);
    const lead = at >= 0 ? words.slice(0, at + 1).join(' ') : program;
    const sub = args[0] && /^[A-Za-z][\w:.-]*$/.test(args[0]) ? ` ${args[0]}` : '';
    return `${lead}${sub}:*`;
  }
  if (t === 'webfetch') {
    const m = /^(https?:\/\/[^/]+)/i.exec(subject);
    return m ? `${m[1]}/*` : subject;
  }
  if (FILE_WRITE_TOOLS.has(t) || t === 'read') {
    const rel = cwd ? relativeTo(subject, cwd) : null;
    const p = (rel ?? subject).replace(/\\/g, '/');
    const slash = p.lastIndexOf('/');
    return slash > 0 ? `${p.slice(0, slash)}/*` : p;
  }
  return '';
}
